import { CartErrorEvent, CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { sectionRenderer } from '@theme/section-renderer';

const OFFER_CONFIG_BY_HANDLE = {
  '3-piece-eyebrow-concealer-eyeshadow-brush-set-black': { id: 'offer-3pc-brush', code: 'MAROOM-MULTI-3PC-100', saveCents: 10000 },
  'real-techniques-everyday-essentials-makeup-brush-set-with-bonus-miracle-complexion-sponge': { id: 'offer-rt-set', code: 'MAROOM-MULTI-RT-250', saveCents: 25000 },
  'olibolla-peel-off-plump-lip-liner': { id: 'offer-olibolla-liner', code: 'MAROOM-MULTI-LINER-50', saveCents: 5000 },
  'olibolla-transfer-proof-lip-glaze': { id: 'offer-olibolla-glaze', code: 'MAROOM-MULTI-GLAZE-50', saveCents: 5000 },
  'vitamin-e-lip-glow-butter': { id: 'offer-lipglow', code: 'MAROOM-MULTI-LIPGLOW-50', saveCents: 5000 },
  'ever-beauty-contour-clubbing-eyepencil-waterproof-lasting-up-to-24h': { id: 'offer-ever-eye', code: 'MAROOM-MULTI-EVER-EYE-25', saveCents: 2500 },
  'rose-berry-eye-colour-magic-liner-duo': { id: 'offer-rose-liner', code: 'MAROOM-MULTI-ROSE-LINER-75', saveCents: 7500 },
  'olibolla-buttery-buff-contour-bronzer-stick': { id: 'offer-contour', code: 'MAROOM-MULTI-CONTOUR-50', saveCents: 5000 },
  'ya-xiu-4-in-1-detachable-makeup-brush': { id: 'offer-yaxiu-brush', code: 'MAROOM-MULTI-YAXIU-100', saveCents: 10000 },
};

const MAX_ACTIVE_UPSELL_OFFERS = 3;
const LEGACY_UPSELL_CODES = ['MAROOMQA-3PC-100', 'MAROOMQA-RT-250', 'MAROOMQA-LINER-50', 'MAROOMQA-GLAZE-50', 'MAROOMQA-LIPGLOW-50', 'MAROOMQA-EVER-EYE-25', 'MAROOMQA-ROSE-LINER-75', 'MAROOMQA-CONTOUR-50', 'MAROOMQA-YAXIU-100', 'MAROOM-YAXIU-100', 'MAROOM-3PC-BRUSH-100', 'MAROOM-RT-SET-250', 'MAROOM-OLIBOLLA-LINER-50', 'MAROOM-OLIBOLLA-GLAZE-50', 'MAROOM-LIPGLOW-50', 'MAROOM-EVER-EYE-25', 'MAROOM-ROSE-LINER-75', 'MAROOM-CONTOUR-50'];

function managedDiscountCodes() {
  return new Set([...LEGACY_UPSELL_CODES, ...Object.values(OFFER_CONFIG_BY_HANDLE).map((config) => config.code)].map((code) => code.toUpperCase()));
}

let cartOfferAdding = false;
let productOfferRequestPending = false;

const PRODUCT_OFFER_INPUTS = [
  ['properties[_marom_upsell_v2_code]', 'code'],
  ['properties[_marom_upsell_v2_save_cents]', 'save'],
  ['properties[_marom_upsell_v2_source]', 'source'],
];

function formatMoney(cents) {
  const amount = Math.max(Number(cents) || 0, 0) / 100;
  const options = amount % 1 === 0 ? { maximumFractionDigits: 0 } : { minimumFractionDigits: 2, maximumFractionDigits: 2 };
  return `EGP ${amount.toLocaleString('en-US', options)}`;
}

function getCartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((item) => (item instanceof HTMLElement ? item.dataset.sectionId : null))
    .filter(Boolean);
}

async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error('cart');
  return response.json();
}

function itemProperties(item) {
  return item?.properties && typeof item.properties === 'object' ? item.properties : {};
}

function itemOfferConfig(item) {
  return item?.handle ? OFFER_CONFIG_BY_HANDLE[item.handle] : null;
}

function hasNormalQualifier(cart) {
  return Boolean(cart.items?.some((item) => !itemProperties(item)._marom_upsell_v2_code &&
    !itemProperties(item)._marom_upsell_v2_save_cents && Number(item.quantity ?? 1) > 0 &&
    Number(item.original_price || item.price || 0) > 0));
}

function isValidV2OfferLine(cart, item) {
  const config = itemOfferConfig(item);
  if (!config) return false;

  const properties = itemProperties(item);
  if (properties._marom_upsell_v2_code !== config.code) return false;
  if (Number(properties._marom_upsell_v2_save_cents || 0) !== config.saveCents) return false;

  if (Number(item.original_price || item.price || 0) <= config.saveCents) return false;

  return hasNormalQualifier(cart);
}

function activeUpsellOffers(cart) {
  const offers = new Map();
  for (const item of cart.items || []) {
    if (isValidV2OfferLine(cart, item)) {
      const config = itemOfferConfig(item);
      if (!offers.has(config.code)) offers.set(config.code, { code: config.code, item });
    }
  }
  return [...offers.values()];
}

function desiredCartDiscountCodes(cart) {
  const managed = managedDiscountCodes();
  const unrelated = (cart.discount_codes || []).map((entry) => entry.code).filter((code) => code && !managed.has(code.toUpperCase()));
  return [...new Set([...unrelated, ...activeUpsellOffers(cart).slice(0, MAX_ACTIVE_UPSELL_OFFERS).map((offer) => offer.code)].map((code) => code.toUpperCase()))];
}

let discountSyncQueue = Promise.resolve();
function syncCartOfferDiscounts() {
  const operation = discountSyncQueue.catch(() => {}).then(async () => {
    const cart = await fetchCart();
    const desired = desiredCartDiscountCodes(cart);
    const current = [...new Set((cart.discount_codes || []).map((entry) => entry.code.toUpperCase()))];
    if (desired.length === current.length && desired.every((code) => current.includes(code))) return { cart, changed: false };
    const response = await fetch(`${Theme.routes.cart_url}/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ discount: desired.join(',') }),
    });
    if (!response.ok) throw new Error('discount');
    return { cart: await response.json(), changed: true };
  });
  discountSyncQueue = operation;
  return operation;
}

async function refreshDiscountSections() {
  for (const sectionId of getCartSectionIds()) {
    if (sectionId === 'cart-drawer-section') {
      const wasOpen = Boolean(document.querySelector('#cart-drawer dialog[open]'));
      await replaceCartDrawerSection(sectionId);
      if (wasOpen) document.getElementById('cart-drawer')?.open?.();
    }
    else await sectionRenderer.renderSection(sectionId, { cache: false, mode: 'hydration' });
  }
}

async function reconcileCartOffers() {
  const result = await syncCartOfferDiscounts();
  if (result.changed) await refreshDiscountSections();
  return result.cart;
}

function allOfferDiscountsApplied(cart) {
  const active = activeUpsellOffers(cart);
  if (active.length > MAX_ACTIVE_UPSELL_OFFERS) return false;
  const allowed = new Set(active.map((offer) => offer.code));
  if ((cart.discount_codes || []).some((entry) => managedDiscountCodes().has(entry.code.toUpperCase()) && !allowed.has(entry.code.toUpperCase()))) return false;
  return active.every((offer) => {
    const allocated = (cart.items || []).reduce((sum, item) => sum +
      (item.line_level_discount_allocations || []).reduce((lineSum, allocation) =>
        lineSum + (allocation.discount_application?.title?.toUpperCase() === offer.code ? Number(allocation.amount || 0) : 0), 0), 0);
    return allocated === itemOfferConfig(offer.item).saveCents;
  });
}

async function fetchOfferProduct(handle) {
  const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error('product');
  return response.json();
}

function selectedOfferVariant(card) {
  const control = card.querySelector('[data-marom-upsell-variant]');
  if (!(control instanceof HTMLInputElement) && !(control instanceof HTMLSelectElement)) return null;
  if (!control.value) return null;

  const selectedOption = control instanceof HTMLSelectElement ? control.selectedOptions[0] : control;
  return {
    id: control.value,
    regularPrice: Number(selectedOption?.dataset.regularPrice || 0),
    offerPrice: Number(selectedOption?.dataset.offerPrice || 0),
  };
}

function cartOfferDetailsUrl(card) {
  const handle = card.dataset.maromUpsellHandle || '';
  const config = OFFER_CONFIG_BY_HANDLE[handle];
  const productUrl = card.dataset.maromUpsellProductUrl || (handle ? `/products/${handle}` : '');
  if (!config?.id || !handle || !productUrl) return '';

  const url = new URL(productUrl, window.location.origin);
  const variant = selectedOfferVariant(card);
  const variantId = variant?.id || card.dataset.maromUpsellDefaultVariantId || '';
  url.searchParams.set('marom_offer', config.id);
  url.searchParams.set('marom_offer_handle', handle);
  if (variantId) url.searchParams.set('variant', variantId);

  return `${url.pathname}${url.search}${url.hash}`;
}

function updateCardDetailsLinks(card) {
  const href = cartOfferDetailsUrl(card);
  if (!href) return;
  for (const link of card.querySelectorAll('[data-marom-upsell-details-link]')) {
    if (link instanceof HTMLAnchorElement) link.href = href;
  }
}

function updateCardFromVariant(card) {
  const variant = selectedOfferVariant(card);
  const button = card.querySelector('[data-marom-upsell-add]');
  const regular = card.querySelector('[data-marom-upsell-regular]');
  const price = card.querySelector('[data-marom-upsell-price]');

  if (regular && variant?.regularPrice) regular.textContent = formatMoney(variant.regularPrice);
  if (price && variant?.offerPrice) price.textContent = formatMoney(variant.offerPrice);

  if (button instanceof HTMLButtonElement) {
    button.disabled = !variant || variant.offerPrice <= 0 || button.dataset.busy === 'true';
    button.textContent = variant ? 'Add to Cart' : 'Select a shade to unlock this offer';
  }

  updateCardDetailsLinks(card);
}

function setCardStatus(card, message) {
  const status = card.querySelector('[data-marom-upsell-status]');
  if (status) status.textContent = message;
}

async function replaceCartDrawerSection(sectionId) {
  const currentSection = document.getElementById(`shopify-section-${sectionId}`);
  if (!currentSection) throw new Error('cart-drawer-section missing');

  const url = new URL(window.location.href);
  url.searchParams.set('section_id', sectionId);
  url.searchParams.sort();

  const response = await fetch(url.toString(), {
    credentials: 'same-origin',
    headers: { Accept: 'text/html' },
  });

  if (!response.ok) throw new Error('cart-drawer-section fetch failed');

  const html = await response.text();
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const freshSection = doc.getElementById(`shopify-section-${sectionId}`);

  if (freshSection) {
    currentSection.replaceWith(freshSection);
    return;
  }

  currentSection.innerHTML = html;
}

async function refreshCartDrawerFromAdd(variantId) {
  const cart = await fetchCart();
  const sectionId = 'cart-drawer-section';
  const deferred = CartLinesUpdateEvent.createPromise();

  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: String(variantId), quantity: 1 }],
      promise: deferred.promise,
    })
  );

  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: {
      didError: false,
      items: cart.items,
      itemCount: cart.item_count,
      sections: {},
      source: 'marom-smart-upsell-v2',
    },
  });

  try {
    await replaceCartDrawerSection(sectionId);
  } catch (error) {
    try {
      await sectionRenderer.renderSection(sectionId, { cache: false, mode: 'hydration' });
    } catch (sectionRendererError) {
      console.warn('[marom-smart-upsell-v2] Cart drawer refresh failed:', error, sectionRendererError);
    }
  }

  document.getElementById('cart-drawer')?.open?.();
}

async function addCartDrawerOffer(card) {
  const button = card.querySelector('[data-marom-upsell-add]');
  if (!(button instanceof HTMLButtonElement) || button.dataset.busy === 'true' || cartOfferAdding) return;

  const handle = card.dataset.maromUpsellHandle || '';
  const config = OFFER_CONFIG_BY_HANDLE[handle];
  const code = config?.code || '';
  const saveCents = config?.saveCents || 0;
  const variant = selectedOfferVariant(card);

  if (!code || !handle || !variant || variant.offerPrice <= 0) {
    updateCardFromVariant(card);
    return;
  }

  cartOfferAdding = true;
  button.dataset.busy = 'true';
  button.disabled = true;
  button.textContent = 'Adding...';
  setCardStatus(card, 'Adding...');

  try {
    const currentCart = await fetchCart();
    const productAlreadyPresent = currentCart.items?.some((item) => item.handle === handle);
    const hasQualifier = hasNormalQualifier(currentCart);

    if (productAlreadyPresent || !hasQualifier || activeUpsellOffers(currentCart).length >= MAX_ACTIVE_UPSELL_OFFERS) {
      setCardStatus(card, 'This offer is no longer available with your current cart.');
      button.textContent = 'This offer is no longer available with your current cart.';
      return;
    }

    const product = await fetchOfferProduct(handle);
    const liveVariant = product.variants?.find((item) => String(item.id) === String(variant.id));
    if (!liveVariant?.available || Number(liveVariant.price) <= saveCents) throw new Error('variant');

    const sectionIds = getCartSectionIds();
    const payload = {
      items: [
        {
          id: Number(variant.id),
          quantity: 1,
          properties: {
            _marom_upsell_v2_code: code,
            _marom_upsell_v2_save_cents: String(saveCents),
            _marom_upsell_v2_source: 'cart-drawer',
          },
        },
      ],
      sections: sectionIds.join(','),
      sections_url: window.location.pathname,
    };

    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload),
    }).then((res) => res.json());

    if (response.status) throw new Error('add');

    await syncCartOfferDiscounts();
    button.textContent = 'Added to cart';
    setCardStatus(card, 'Added to cart');
    await refreshCartDrawerFromAdd(variant.id);
  } catch (error) {
    document.dispatchEvent(
      new CartErrorEvent({
        error: 'This offer is no longer available with your current cart.',
        code: 'SERVICE_UNAVAILABLE',
      })
    );
    button.dataset.busy = 'false';
    button.disabled = false;
    button.textContent = 'Add to Cart';
    setCardStatus(card, 'This offer is no longer available with your current cart.');
  } finally {
    cartOfferAdding = false;
    button.dataset.busy = 'false';
  }
}

async function addRecommendedTool(card) {
  const button = card.querySelector('[data-marom-tool-add]');
  if (!button || button.disabled) return;
  button.disabled = true;
  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(card.dataset.variantId), quantity: 1 }] }),
    });
    if (!response.ok) throw new Error('add');
    await refreshCartDrawerFromAdd(card.dataset.variantId);
  } catch (error) {
    button.disabled = false;
    card.querySelector('[role="status"]').textContent = 'Unable to add. Please try again.';
  }
}

function currentProductVariantId() {
  const input = document.querySelector('product-form-component input[name="id"]');
  return input instanceof HTMLInputElement ? input.value : '';
}

function productOfferVariants(panel) {
  const script = panel.querySelector('[data-marom-product-offer-variants]');
  if (!script) return [];

  try {
    return JSON.parse(script.textContent || '[]');
  } catch (error) {
    return [];
  }
}

function updateProductOfferPrice(panel) {
  const variants = productOfferVariants(panel);
  const variantId = currentProductVariantId();
  const variant = variants.find((item) => String(item.id) === String(variantId)) || variants[0];
  if (!variant) return;

  const saveCents = Number(panel.dataset.maromOfferSaveCents || 0);
  const regular = panel.querySelector('[data-marom-product-offer-regular]');
  const price = panel.querySelector('[data-marom-product-offer-price]');
  if (regular) regular.textContent = formatMoney(variant.price);
  if (!variant.available || Number(variant.price) <= saveCents) {
    panel.hidden = true;
    removeProductOfferInputs();
    return;
  }
  if (price) price.textContent = formatMoney(Number(variant.price) - saveCents);
}

function addProductOfferInputs(panel) {
  const handle = panel.dataset.maromOfferHandle || '';
  const config = OFFER_CONFIG_BY_HANDLE[handle];
  const values = {
    code: config?.code || '',
    save: String(config?.saveCents || panel.dataset.maromOfferSaveCents || ''),
    source: 'product-page',
  };
  const validVariantIds = new Set(productOfferVariants(panel).map((variant) => String(variant.id)));

  for (const form of document.querySelectorAll('product-form-component form')) {
    const variantInput = form.querySelector('input[name="id"]');
    const variantId = variantInput instanceof HTMLInputElement ? String(variantInput.value) : '';
    const chosenVariant = productOfferVariants(panel).find((variant) => String(variant.id) === variantId);
    if (!validVariantIds.has(variantId) || !chosenVariant?.available || chosenVariant.price <= config.saveCents) {
      form.querySelectorAll('[data-marom-product-offer-input]').forEach((input) => input.remove());
      continue;
    }

    for (const [name, key] of PRODUCT_OFFER_INPUTS) {
      let input = form.querySelector(`input[name="${name}"]`);
      if (!(input instanceof HTMLInputElement)) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.dataset.maromProductOfferInput = 'true';
        form.appendChild(input);
      }
      input.value = values[key];
    }
  }
}

function removeProductOfferInputs() {
  document.querySelectorAll('[data-marom-product-offer-input]').forEach((input) => input.remove());
}

function positionProductOfferPanel(panel) {
  const buyButtons = document.querySelector('.product-details .buy-buttons-block');
  if (buyButtons?.parentNode && panel.parentNode !== buyButtons.parentNode) {
    buyButtons.parentNode.insertBefore(panel, buyButtons);
  } else if (buyButtons?.parentNode && panel.nextElementSibling !== buyButtons) {
    buyButtons.parentNode.insertBefore(panel, buyButtons);
  }
}

async function validateProductOfferPanel(panel) {
  const params = new URLSearchParams(window.location.search);
  const handle = panel.dataset.maromOfferHandle || '';
  const config = OFFER_CONFIG_BY_HANDLE[handle];

  if (!config?.id || !config?.code || params.get('marom_offer') !== config.id || params.get('marom_offer_handle') !== handle) {
    panel.hidden = true;
    removeProductOfferInputs();
    return;
  }

  const requestedVariantId = params.get('variant') || '';
  const variants = productOfferVariants(panel);
  const requestedVariant = requestedVariantId
    ? variants.find((variant) => String(variant.id) === String(requestedVariantId))
    : null;

  if (requestedVariantId && (!requestedVariant?.available || requestedVariant.price <= config.saveCents)) {
    panel.hidden = true;
    removeProductOfferInputs();
    return;
  }

  try {
    const cart = await fetchCart();
    const hasQualifier = hasNormalQualifier(cart);
    const productAlreadyPresent = cart.items?.some((item) => item.handle === handle);

    if (!hasQualifier || productAlreadyPresent || activeUpsellOffers(cart).length >= MAX_ACTIVE_UPSELL_OFFERS) {
      panel.hidden = true;
      removeProductOfferInputs();
      return;
    }

    positionProductOfferPanel(panel);
    panel.hidden = false;
    updateProductOfferPrice(panel);
    if (!panel.hidden) addProductOfferInputs(panel);
  } catch (error) {
    panel.hidden = true;
    removeProductOfferInputs();
  }
}

function initProductOfferPanel() {
  const panel = document.querySelector('[data-marom-product-offer-panel]');
  if (!(panel instanceof HTMLElement)) return;
  validateProductOfferPanel(panel);
}

// Recheck deep-link eligibility at submission, including stale tabs.
const approvedOfferForms = new WeakSet();
async function prepareProductOffer(event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.querySelector('[data-marom-product-offer-input]')) return;
  if (approvedOfferForms.has(form)) {
    approvedOfferForms.delete(form);
    return;
  }
  event.preventDefault();
  event.stopImmediatePropagation();
  if (cartOfferAdding) return;
  cartOfferAdding = true;
  let submitted = false;
  try {
    const panel = document.querySelector('[data-marom-product-offer-panel]');
    if (!(panel instanceof HTMLElement)) return;
    await validateProductOfferPanel(panel);
    if (panel.hidden || !form.querySelector('[data-marom-product-offer-input]')) return;
    approvedOfferForms.add(form);
    productOfferRequestPending = true;
    form.requestSubmit(event.submitter);
    submitted = true;
  } catch (error) {
    approvedOfferForms.delete(form);
    removeProductOfferInputs();
  } finally {
    if (!submitted) {
      productOfferRequestPending = false;
      cartOfferAdding = false;
    }
  }
}
document.addEventListener('submit', prepareProductOffer, true);

let checkoutDiscountApplying = false;

async function prepareCheckoutDiscount(event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || form.id !== 'cart-form') return;

  const submitter = event.submitter;
  if (!(submitter instanceof HTMLButtonElement) || submitter.name !== 'checkout') return;
  event.preventDefault();
  if (checkoutDiscountApplying || cartOfferAdding) return;
  checkoutDiscountApplying = true;
  submitter.disabled = true;

  try {
    const result = await syncCartOfferDiscounts();
    if (!allOfferDiscountsApplied(result.cart)) throw new Error('offer allocations');
  } catch (error) {
    checkoutDiscountApplying = false;
    submitter.disabled = false;
    document.dispatchEvent(new CartErrorEvent({ error: 'Unable to verify cart discounts. Please try again.', code: 'SERVICE_UNAVAILABLE' }));
    return;
  }
  {
    let checkoutInput = form.querySelector('input[data-marom-v2-checkout]');
    if (!(checkoutInput instanceof HTMLInputElement)) {
      checkoutInput = document.createElement('input');
      checkoutInput.type = 'hidden';
      checkoutInput.name = 'checkout';
      checkoutInput.dataset.maromV2Checkout = 'true';
      form.appendChild(checkoutInput);
    }

    HTMLFormElement.prototype.submit.call(form);
  }
}

document.addEventListener('change', (event) => {
  const target = event.target;
  if (target instanceof Element && target.matches('[data-marom-upsell-variant]')) {
    const card = target.closest('[data-marom-upsell-card]');
    if (card instanceof HTMLElement) updateCardFromVariant(card);
  }
});

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const toolButton = target.closest('[data-marom-tool-add]');
  if (toolButton) addRecommendedTool(toolButton.closest('[data-marom-tool-card]'));
  const button = target.closest('[data-marom-upsell-add]');
  if (button instanceof HTMLButtonElement) {
    const card = button.closest('[data-marom-upsell-card]');
    if (card instanceof HTMLElement) addCartDrawerOffer(card);
  }
});

document.addEventListener('submit', prepareCheckoutDiscount, true);

document.addEventListener(StandardEvents.productSelect, () => {
  const panel = document.querySelector('[data-marom-product-offer-panel]');
  if (panel instanceof HTMLElement && !panel.hidden) {
    window.requestAnimationFrame(() => {
      updateProductOfferPrice(panel);
      addProductOfferInputs(panel);
    });
  }
});

document.addEventListener(StandardEvents.cartLinesUpdate, async (event) => {
  const completesProductOffer = productOfferRequestPending && event.context === 'product' && event.action === 'add';
  try {
    await event.promise;
  } catch (error) {
    void error;
  } finally {
    if (completesProductOffer) {
      productOfferRequestPending = false;
      cartOfferAdding = false;
    }
  }
  try {
    await reconcileCartOffers();
  } catch (error) {
    document.dispatchEvent(new CartErrorEvent({ error: 'Unable to refresh cart discounts. Please try again.', code: 'SERVICE_UNAVAILABLE' }));
  }
  const panel = document.querySelector('[data-marom-product-offer-panel]');
  if (panel instanceof HTMLElement) validateProductOfferPanel(panel);
});

document.addEventListener('DOMContentLoaded', async () => {
  try { await reconcileCartOffers(); } catch (error) { void error; }
  for (const card of document.querySelectorAll('[data-marom-upsell-card]')) {
    if (card instanceof HTMLElement) updateCardFromVariant(card);
  }
  initProductOfferPanel();
});
